import * as React from "react"
import { Head, router, usePage } from "@inertiajs/react"
import { RefreshCw, Search, Upload } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { ClipCard } from "@/components/clips/ClipCard"
import { ClipListRow } from "@/components/clips/ClipListRow"
import { ClipsBulkActionBar } from "@/components/clips/ClipsBulkActionBar"
import { ClipsHeader } from "@/components/clips/ClipsHeader"
import { ClipsListHeaderCell } from "@/components/clips/ClipsListHeaderCell"
import { ClipsSubTabs } from "@/components/clips/ClipsSubTabs"
import { ClipsSyncStatusBanner } from "@/components/clips/ClipsSyncStatusBanner"
import { ClipsSearchDialog, useSearchHotkey } from "@/components/clips/ClipsSearchDialog"
import { ClipUploadDialog } from "@/components/clips/ClipUploadDialog"
import {
  COLUMN_DEFAULT_DIRECTION,
  type Clip,
  type ClipFilters,
  type ClipSortDirection,
  type ClipSortKey,
  type ClipSortableColumn,
  type ClipTypeRef,
  type ClipView,
  type DropboxIntegrationState,
  type SyncStatus,
  type TagRef,
} from "@/components/clips/types"
import type { PageProps } from "@/types/inertia"

const LIST_SORT_STORAGE_KEY = "tubery.clips.list-sort"
const VIEW_STORAGE_KEY = "tubery.clips.view"

type ClipsPageProps = PageProps<{
  clips: Clip[]
  total_count: number
  filters: ClipFilters
  sort: ClipSortKey
  direction: ClipSortDirection
  view: ClipView
  all_tags: TagRef[]
  folders: string[]
  clip_types: ClipTypeRef[]
  needs_processing_count: number
  queued_for_enrichment_count: number
  awaiting_review_count: number
  dropbox: DropboxIntegrationState
  sync_status: SyncStatus
}>

export default function Clips() {
  const { props } = usePage<ClipsPageProps>()
  const {
    clips, total_count, filters, sort, direction, view, all_tags, folders,
    clip_types, awaiting_review_count, dropbox, sync_status,
  } = props

  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set())
  const [pendingThumbnailIds, setPendingThumbnailIds] = React.useState<Set<number>>(new Set())
  const [uploadOpen, setUploadOpen] = React.useState(false)
  const [searchOpen, setSearchOpen] = React.useState(false)

  // Clear pending-thumbnail markers for clips that have caught up (thumbnails
  // now present) so the spinner doesn't persist after a successful job.
  React.useEffect(() => {
    setPendingThumbnailIds((prev) => {
      if (prev.size === 0) return prev
      const visible = new Map(clips.map((c) => [c.id, c.has_thumbnails]))
      let changed = false
      const next = new Set(prev)
      for (const id of prev) {
        if (visible.get(id) === true) {
          next.delete(id)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [clips])

  function markPendingThumbnails(ids: number[]) {
    if (ids.length === 0) return
    setPendingThumbnailIds((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => next.add(id))
      return next
    })
  }

  function handleRequestThumbnails(clipIds: number[]) {
    if (clipIds.length === 0) return
    markPendingThumbnails(clipIds)
    if (clipIds.length === 1) {
      router.post(`/clips/${clipIds[0]}/extract_thumbnails`, {}, {
        preserveScroll: true,
        preserveState: true,
      })
    } else {
      router.post("/clips/bulk_extract_thumbnails", { clip_ids: clipIds }, {
        preserveScroll: true,
        preserveState: true,
      })
    }
  }

  useSearchHotkey(React.useCallback(() => setSearchOpen(true), []))

  // Restore remembered sort on first visit. Only restores when the URL doesn't
  // already specify a sort — explicit URL sorts always win.
  React.useEffect(() => {
    if (typeof window === "undefined") return
    const url = new URL(window.location.href)
    if (url.searchParams.has("sort")) return
    const stored = window.localStorage.getItem(LIST_SORT_STORAGE_KEY)
    if (!stored) return
    try {
      const { sort: storedSort, direction: storedDirection } = JSON.parse(stored) as {
        sort?: ClipSortKey
        direction?: ClipSortDirection
      }
      if (!storedSort) return
      if (storedSort === sort && storedDirection === direction) return
      router.get("/clips", { ...filters, sort: storedSort, direction: storedDirection ?? "desc", view }, {
        preserveState: true, preserveScroll: true, replace: true,
      })
    } catch {
      window.localStorage.removeItem(LIST_SORT_STORAGE_KEY)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Restore remembered view (grid/list) on first visit. Only restores when the
  // URL doesn't already specify a view.
  React.useEffect(() => {
    if (typeof window === "undefined") return
    const url = new URL(window.location.href)
    if (url.searchParams.has("view")) return
    const stored = window.localStorage.getItem(VIEW_STORAGE_KEY)
    if (stored !== "grid" && stored !== "list") return
    if (stored === view) return
    router.get("/clips", { ...filters, sort, direction, view: stored }, {
      preserveState: true, preserveScroll: true, replace: true,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Persist the current view whenever it changes, so the next visit (or sidebar
  // nav back to /clips) reopens in the same mode.
  React.useEffect(() => {
    if (typeof window === "undefined") return
    window.localStorage.setItem(VIEW_STORAGE_KEY, view)
  }, [view])

  function persistSort(nextSort: ClipSortKey, nextDirection: ClipSortDirection) {
    if (typeof window === "undefined") return
    window.localStorage.setItem(
      LIST_SORT_STORAGE_KEY,
      JSON.stringify({ sort: nextSort, direction: nextDirection }),
    )
  }

  function handleColumnSort(column: ClipSortableColumn) {
    const isActive = sort === column
    const nextDirection: ClipSortDirection = isActive
      ? direction === "asc" ? "desc" : "asc"
      : COLUMN_DEFAULT_DIRECTION[column]
    persistSort(column, nextDirection)
    router.get("/clips", { ...filters, sort: column, direction: nextDirection, view }, {
      preserveState: true, preserveScroll: true, replace: true,
    })
  }

  function toggleSelect(clipId: number, selected: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (selected) next.add(clipId)
      else next.delete(clipId)
      return next
    })
  }

  function handleSync() {
    router.post("/clips/trigger_sync", {}, { preserveScroll: true })
  }

  const selectedArray = React.useMemo(() => Array.from(selectedIds), [selectedIds])
  const visibleClipIds = React.useMemo(() => clips.map((c) => c.id), [clips])
  const selectAllState: "none" | "some" | "all" = React.useMemo(() => {
    if (visibleClipIds.length === 0) return "none"
    const selectedVisible = visibleClipIds.filter((id) => selectedIds.has(id)).length
    if (selectedVisible === 0) return "none"
    if (selectedVisible === visibleClipIds.length) return "all"
    return "some"
  }, [selectedIds, visibleClipIds])

  function toggleSelectAll() {
    if (selectAllState === "all") {
      // Deselect just the visible ids (preserve any off-page selections — none today, but future-proof).
      setSelectedIds((prev) => {
        const next = new Set(prev)
        visibleClipIds.forEach((id) => next.delete(id))
        return next
      })
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        visibleClipIds.forEach((id) => next.add(id))
        return next
      })
    }
  }

  return (
    <>
      <Head title="Clips">
        <meta name="description" content="Dropbox-backed B-roll library with search, tags, and pending-enrichment review." />
        <meta property="og:title" content="Clips" />
        <meta property="og:description" content="Dropbox-backed B-roll library with search, tags, and pending-enrichment review." />
      </Head>
      <AppShell maxWidth="6xl">
        <PageHeader
          title="Clips"
          description="Search, tag, and enrich B-roll clips backed by Dropbox."
          actions={
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSearchOpen(true)}
                aria-label="Search clips (press / )"
                title="Search clips (press / )"
              >
                <Search className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={handleSync}
                disabled={!dropbox.configured}
              >
                <RefreshCw className="mr-1 h-4 w-4" /> Sync
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={() => setUploadOpen(true)}
                disabled={!dropbox.configured}
              >
                <Upload className="mr-1 h-4 w-4" /> Add clip
              </Button>
            </div>
          }
        />

        <div className="mt-6 space-y-4">
          <ClipsSyncStatusBanner dropbox={dropbox} sync={sync_status} />
          <ClipsSubTabs active="browse" awaitingReviewCount={awaiting_review_count} />

          <ClipsHeader
            filters={filters}
            sort={sort}
            view={view}
            allTags={all_tags}
            allFolders={folders}
            clipTypes={clip_types}
            totalCount={total_count}
            selectAllState={selectAllState}
            onToggleSelectAll={toggleSelectAll}
            bulkActions={
              <ClipsBulkActionBar
                selectedIds={selectedArray}
                onClear={() => setSelectedIds(new Set())}
                onRequestThumbnails={handleRequestThumbnails}
              />
            }
          />

          {view === "grid" ? (
            <GridView
              clips={clips}
              selectedIds={selectedIds}
              pendingThumbnailIds={pendingThumbnailIds}
              onSelectChange={toggleSelect}
              onRequestThumbnails={handleRequestThumbnails}
            />
          ) : (
            <ListView
              clips={clips}
              selectedIds={selectedIds}
              pendingThumbnailIds={pendingThumbnailIds}
              onSelectChange={toggleSelect}
              onRequestThumbnails={handleRequestThumbnails}
              currentSort={sort}
              currentDirection={direction}
              onColumnSort={handleColumnSort}
            />
          )}
        </div>

        <ClipUploadDialog
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          brollRootPath={dropbox.broll_root_path}
          clipTypes={clip_types}
        />

        <ClipsSearchDialog
          open={searchOpen}
          onOpenChange={setSearchOpen}
          initialQuery={filters.q}
          filters={filters}
          sort={sort}
          view={view}
        />
      </AppShell>
    </>
  )
}

function GridView({
  clips, selectedIds, pendingThumbnailIds, onSelectChange, onRequestThumbnails,
}: {
  clips: Clip[]
  selectedIds: Set<number>
  pendingThumbnailIds: Set<number>
  onSelectChange: (id: number, selected: boolean) => void
  onRequestThumbnails: (ids: number[]) => void
}) {
  if (clips.length === 0) return <EmptyState />
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {clips.map((clip) => (
        <ClipCard
          key={clip.id}
          clip={clip}
          selected={selectedIds.has(clip.id)}
          pendingThumbnail={pendingThumbnailIds.has(clip.id)}
          onSelectChange={onSelectChange}
          onRequestThumbnails={onRequestThumbnails}
        />
      ))}
    </div>
  )
}

function ListView({
  clips,
  selectedIds,
  pendingThumbnailIds,
  onSelectChange,
  onRequestThumbnails,
  currentSort,
  currentDirection,
  onColumnSort,
}: {
  clips: Clip[]
  selectedIds: Set<number>
  pendingThumbnailIds: Set<number>
  onSelectChange: (id: number, selected: boolean) => void
  onRequestThumbnails: (ids: number[]) => void
  currentSort: ClipSortKey
  currentDirection: ClipSortDirection
  onColumnSort: (column: ClipSortableColumn) => void
}) {
  if (clips.length === 0) return <EmptyState />
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[960px] text-left">
        <thead>
          <tr className="border-b border-hairline">
            <th className="w-8 p-2"></th>
            <th className="w-32 p-2"></th>
            <ClipsListHeaderCell column="title"    label="Title"    currentSort={currentSort} currentDirection={currentDirection} onSort={onColumnSort} />
            <ClipsListHeaderCell column="duration" label="Duration" currentSort={currentSort} currentDirection={currentDirection} onSort={onColumnSort} className="w-28 pl-6" />
            <ClipsListHeaderCell column="recorded" label="Recorded" currentSort={currentSort} currentDirection={currentDirection} onSort={onColumnSort} className="w-32 pl-6" />
            <ClipsListHeaderCell column="status"   label="Status"   currentSort={currentSort} currentDirection={currentDirection} onSort={onColumnSort} className="w-32 pl-6" />
            <th className="w-10 p-2"></th>
          </tr>
        </thead>
        <tbody>
          {clips.map((clip) => (
            <ClipListRow
              key={clip.id}
              clip={clip}
              selected={selectedIds.has(clip.id)}
              pendingThumbnail={pendingThumbnailIds.has(clip.id)}
              onSelectChange={onSelectChange}
              onRequestThumbnails={onRequestThumbnails}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="rounded-lg border border-dashed border-hairline bg-surface p-8 text-center text-ink-muted">
      No clips match your filters. Try widening the status filter or clearing search.
    </div>
  )
}
